import { type CountryCode } from "@/data/country-data"

function getClientCookie(name: string): string | undefined {
    if (typeof document === "undefined") return undefined

    const prefix = `${name}=`
    const row = document.cookie.split("; ").find(cookie => cookie.startsWith(prefix))
    if (!row) return undefined

    return row.slice(prefix.length)
}

function setClientCookie(name: string, value: string, maxAge: number): void {
    if (typeof document === "undefined") return

    document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; samesite=lax; Secure`
}

export function getUserCountryOriginClient(): CountryCode | null {
    const raw = getClientCookie("user_country_origin")

    if (!raw) return null

    try {
        const parsed = JSON.parse(decodeURIComponent(raw))
        return parsed.countryCode.toUpperCase() ?? null
    } catch (error) {
        console.info("Failed to parse user_country_origin cookie:", error)
        return null
    }
}

export function getBasketSessionCountryClient(): string | null {
    const raw = getClientCookie("basket_session")
    if (!raw) return null

    try {
        const parsed = JSON.parse(decodeURIComponent(raw)) as {
            metadata?: { country?: { code?: string } }
        }
        const code = parsed.metadata?.country?.code
        return typeof code === "string" && code.trim() ? code.trim().toUpperCase() : null
    } catch {
        return null
    }
}

export function getDrainIdClient(): string | null {
    return getClientCookie("drainId") ?? null
}

export function setDrainIdClient(drainId: string) {
    setClientCookie("drainId", drainId, 60 * 60 * 24 * 7)
}

const TRACKED_TRANSACTIONS_COOKIE = "tracked_transactions"
const ONE_WEEK_SECONDS = 60 * 60 * 24 * 7

function getTrackedTransactions(): Record<string, boolean> {
    const raw = getClientCookie(TRACKED_TRANSACTIONS_COOKIE)

    if (!raw) return {}

    try {
        return JSON.parse(decodeURIComponent(raw))
    } catch {
        return {}
    }
}

function setTrackedTransactionsCookie(data: Record<string, boolean>) {
    setClientCookie(
        TRACKED_TRANSACTIONS_COOKIE,
        encodeURIComponent(JSON.stringify(data)),
        ONE_WEEK_SECONDS
    )
}

export function initTransactionTracking(transactionId: string): void {
    const existing = getTrackedTransactions()
    if (transactionId in existing) return
    setTrackedTransactionsCookie({ ...existing, [transactionId]: false })
}

export function isTransactionTracked(transactionId: string): boolean {
    return getTrackedTransactions()[transactionId] === true
}

export function markTransactionTracked(transactionId: string): void {
    const existing = getTrackedTransactions()
    setTrackedTransactionsCookie({ ...existing, [transactionId]: true })
}
